import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface Settings {
  organizationName: string;
  lockdownDefaultEnd: 'yesterday' | 'none';
  passwordLoginEnabled: boolean;
  durationIncrementMinutes: 1 | 5 | 15 | 30 | 60;
  /** What happens to a duration off the step: refuse it, or move it. */
  durationRounding: 'none' | 'up' | 'down' | 'nearest';
  defaultBeginTime: string;
  notifyAbsence: boolean;
  notifyTimesheet: boolean;

  /** Where the weekly "who is behind" summary is posted, besides email. */
  chatProvider: 'none' | 'google' | 'slack' | 'teams';
  chatWeeklySummary: boolean;
  /**
   * The webhook URL is NEVER returned - anybody holding it can post to the
   * space. The screen reports whether one is stored and offers to replace it.
   */
  chatWebhookConfigured: boolean;
  /** Write-only: set to store a new webhook, omit to leave the stored one. */
  chatWebhookUrl?: string;
  /** 1-12. The year reports run from this month, not from January. */
  financialYearStartMonth: number;
  /** 'classic' bills the duration as it is; 'decimal' rounds it to two decimal hours. */
  rateMode: 'classic' | 'decimal';
  /** When false, an entry that would exceed a budget is refused. */
  allowOverbookingBudget: boolean;
  overtimeStartDate: string;

  /*
   * Numbering and invoicing. These have always been in the API's schema and have
   * always crossed the wire; they were simply not listed here, so no screen could
   * offer them and the only way to change one was an UPDATE against the table.
   */

  /** `{cc,4}` `{pc,4}` `{ac,4}`. Empty switches numbering off for that kind. */
  customerNumberFormat: string;
  projectNumberFormat: string;
  activityNumberFormat: string;

  invoiceNumberFormat: string;
  invoiceDueDays: number;
  /** 1800 is 18%. Basis points, so 2.5% stays exact. */
  invoiceTaxBasisPoints: number;

  /** Printed on the invoice. Any of them empty leaves that line off. */
  invoiceIssuerAddress: string;
  invoiceTaxNumber: string;
  invoiceBankDetails: string;
  invoicePaymentTerms: string;
}

/** Fixed at deploy time. Reported as configured or not, never echoed. */
export interface EnvironmentInfo {
  organizationTimezone: string;
  allowedEmailDomain: string | null;
  sessionTtlHours: number;
  googleSignInConfigured: boolean;
  mailConfigured: boolean;
  mailFrom: string | null;
  nodeEnv: string;
  appBaseUrl: string;
}

export interface SystemInfo {
  settings: Settings;
  history: { key: string; updatedAt: string; updatedBy: string | null }[];
  environment: EnvironmentInfo;
  counts: {
    people: number;
    active: number;
    entries: number;
    projects: number;
    awaitingApproval: number;
  };
}

export const useSystemInfo = () =>
  useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.get<SystemInfo>('/admin/settings'),
  });

export const useSaveSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Settings>) =>
      api.patch<{ settings: Settings }>('/admin/settings', patch),
    onSuccess: () => {
      // Broad on purpose: the increment changes what a save accepts, the lockdown
      // default changes which days are editable, and the organisation name is on
      // the sign-in screen. Nothing here is worth patching a cache by hand.
      void queryClient.invalidateQueries();
    },
  });
};

/** Prove a webhook reaches the space before it is saved. */
export const useTestChat = () =>
  useMutation({
    mutationFn: (input: { provider: string; url?: string }) =>
      api.post<{ ok: true }>('/admin/settings/chat-test', input),
  });
